import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_PRIVACY_POLICY_API_URL || process.env.REACT_APP_SAVED_FILTERS_API_URL;

// Get user data
const getAuthData = () => {
  try {
    const allKeys = Object.keys(localStorage);

    const lastAuthUserKey = allKeys.find(
      k => k.includes('CognitoIdentityServiceProvider') && k.includes('LastAuthUser'),
    );
    if (!lastAuthUserKey) return { token: null, userId: null };

    const parts = lastAuthUserKey.split('.');
    const clientIdIndex = parts.findIndex(p => p === 'CognitoIdentityServiceProvider') + 1;
    const clientId = parts[clientIdIndex];
    const userSub = localStorage.getItem(lastAuthUserKey);

    if (!clientId || !userSub) return { token: null, userId: null };

    const possibleBaseKeys = [
      `CognitoIdentityServiceProvider.${clientId}.${userSub}`,
      `CognitoIdentityServiceProvider.${clientId}.LastAuthUser.${userSub}`,
    ];

    let idToken = null;
    let accessToken = null;

    for (const keyBase of possibleBaseKeys) {
      const id = localStorage.getItem(`${keyBase}.idToken`);
      const acc = localStorage.getItem(`${keyBase}.accessToken`);
      if (id || acc) {
        idToken = id;
        accessToken = acc;
        break;
      }
    }

    if (!idToken && !accessToken) {
      console.warn('No idToken or accessToken found for clientId:', clientId);
      return { token: null, userId: null };
    }

    const token = idToken || accessToken;
    const payload = parseJwt(token);
    const userId = payload?.sub || userSub || null;

    return { token, userId };
  } catch (e) {
    console.error('Error extracting Cognito tokens:', e);
    return { token: null, userId: null };
  }
};

function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const getAuthToken = () => {
  return getAuthData().token;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  config => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error),
);

/**
 * Get privacy policy acceptance status for the current user
 * @param {string} userId
 * @returns {Promise<{privacyPolicyAccepted: boolean, acceptedAt: string|null}>}
 */
export const getPrivacyPolicyStatus = async userId => {
  try {
    const url = `/privacy-policy/${userId}`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error getting privacy policy status:', error);
    console.error('Error details:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update privacy policy acceptance status for the current user
 * @param {string} userId
 * @param {boolean} accepted
 * @param {boolean} cookiesAnalytics
 * @param {boolean} cookiesPreferences
 * @returns {Promise<{success: boolean, privacyPolicyAccepted: boolean, acceptedAt: string|null}>}
 */
export const updatePrivacyPolicyStatus = async (
  userId,
  accepted,
  cookiesAnalytics = null,
  cookiesPreferences = null,
) => {
  try {
    const payload = { accepted };
    if (cookiesAnalytics !== null) {
      payload.cookiesAnalytics = cookiesAnalytics;
    }
    if (cookiesPreferences !== null) {
      payload.cookiesPreferences = cookiesPreferences;
    }
    const response = await apiClient.put(`/privacy-policy/${userId}`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating privacy policy status:', error);
    throw error;
  }
};
